import { useState, useEffect } from "react"
import { Loader2, Check, Clock, ClipboardList } from "lucide-react"
import SwimmerSidebar from "../components/SwimmerSidebar"
import EmptyState from "../components/EmptyState"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function SwimmerWorkouts() {
  const [workouts, setWorkouts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  async function fetchWorkouts() {
    setIsLoading(true)
    try {
      const res = await fetch(`${API}/api/workouts/today`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to fetch workouts")
      setWorkouts(await res.json())
    } catch (e) {
      setError(e.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchWorkouts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleComplete(id) {
    try {
      const res = await fetch(`${API}/api/workouts/${id}/toggle`, {
        method: "POST",
        headers: authHeaders()
      })
      if (!res.ok) throw new Error("Failed to complete workout")

      setWorkouts(prev => prev.map(w => w.id === id ? { ...w, completed: true } : w))
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <SwimmerSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
        <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("workouts_title")}</h1>
            <p className="mt-1 text-sm text-mist-300">{t("workouts_subtitle")}</p>
          </div>
        </header>

        {error && <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
        ) : (
          <div className="space-y-4">
            {workouts.map(w => (
              <div key={w.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-ink-800/30 p-5 shadow-sm">
                <div>
                  <h3 className="text-lg font-bold text-paper-0">{w.task}</h3>
                  <p className="flex items-center gap-1.5 text-sm text-mist-400 mt-1">
                    <Clock className="h-3.5 w-3.5" />
                    {w.duration_min} {t("workouts_mins")}
                  </p>
                </div>
                <button
                  disabled={w.completed}
                  onClick={() => handleComplete(w.id)}
                  aria-label={w.completed ? "Workout completed" : "Mark workout complete"}
                  className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-800 ${w.completed ? 'bg-emerald-500 text-ink-900 cursor-default' : 'border border-mist-500 text-mist-400 hover:border-emerald-500 hover:text-emerald-400'}`}
                >
                  <Check className="h-5 w-5" strokeWidth={2.5} />
                </button>
              </div>
            ))}
            {workouts.length === 0 && (
              <EmptyState
                icon={<ClipboardList className="h-12 w-12 text-mist-600" />}
                title={t("workouts_empty")}
                hint={t("workouts_empty_hint")}
              />
            )}
          </div>
        )}
      </main>
    </div>
  )
}
